import { api } from './api';
import { getUrlParameter, isNativeOfflineShell } from 'helpers/core/url';
import config from 'config';
import { hasValue, isString } from 'swipeclock-frontend';
import { PUNCH_SUBMIT_HARD_TIMEOUT_MS } from 'modules/webClock/constants/punchSubmitTimeoutConstants';
import { CLOCK_CONNECT_TIMEOUT_MS } from 'modules/webClock/constants/apiReachabilityConstants';
import { getWebClockUserStorageKey } from 'modules/webClock/utils/webClockUserStorageKey';
import { mergeOfflineClockConnectCache } from 'modules/webClock/utils/offlineClockConnectCache';
import { isTransportError } from 'modules/webClock/utils/isNetworkError';
import {
  reportApiReachable,
  reportApiUnreachable,
} from 'modules/webClock/utils/apiReachability';

// NOTE: Read config.clockServiceUrl inside each query/function body rather
// than at module scope. URLs are build-time constants per mobile artifact.

// RTK Query 1.9's `timeout` option aborts the whole thunk, so callers see
// AbortError instead of TIMEOUT_ERROR. Abort only the fetch instead.
export const withRequestTimeout = (request, timeoutMs) => {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  return { ...request, signal: controller.signal };
};

export const toTimeoutError = (error, meta) => {
  if (error?.status === 'FETCH_ERROR' && meta?.request?.signal?.aborted) {
    return { ...error, status: 'TIMEOUT_ERROR' };
  }

  return error;
};

export const buildOfflineMobilePunchRequest = ({ body, punch, user }) =>
  withRequestTimeout(
    {
      url: `${config.clockServiceUrl}/MobileOfflinePunch`,
      method: 'POST',
      headers: {
        jwt: user.token,
        punch,
      },
      body,
    },
    PUNCH_SUBMIT_HARD_TIMEOUT_MS
  );

const buttonList = [
  { label: 'Clock In', value: 'IN', tier: 1, punch: 'PunchIn' },
  { label: 'Clock Out', value: 'OUT', tier: 1, punch: 'PunchOut' },
  { label: 'Start Break', value: 'OUTBREAK', tier: 2, punch: 'StartBreak' },
  { label: 'End Break', value: 'INBREAK', tier: 2, punch: 'EndBreak' },
  { label: 'Start Meal', value: 'OUTMEAL', tier: 2, punch: 'StartMeal' },
  { label: 'End Meal', value: 'INMEAL', tier: 2, punch: 'EndMeal' },
  { label: 'Transfer', value: 'TRANSFER', tier: 2, punch: 'Transfer' },
  { label: 'Portal', value: 'ESS', tier: 2, redirect: 'openESSTab' },
];

export const webclockApi = api.injectEndpoints({
  endpoints: (build) => ({
    clockConnect: build.query({
      query: ({ mobile: isMobile, user }) => {
        const url = isMobile ? '/MobileClockConnect' : '/ClockConnect';
        const request = {
          url: `${config.clockServiceUrl}${url}`,
          method: 'POST',
          headers: {
            jwt: user.token,
          },
          body: {},
        };

        return isMobile
          ? withRequestTimeout(request, CLOCK_CONNECT_TIMEOUT_MS)
          : request;
      },
      transformResponse: async (res) => {
        const isError = res?.errorCode === '-1';
        const errorMessage = res?.errorMessage;
        if (isError) {
          if (isString(errorMessage) && errorMessage.includes('IP=')) {
            const ipAddress = errorMessage.split('IP=')[1] ?? '';
            throw new Error(
              `The WebClock cannot be used from your current location. Contact your manager if you think it should be. IP=${ipAddress}`
            );
          } else {
            throw new Error(
              `${res?.errorMessage || 'Error: unable to access clock data'}`
            );
          }
        }

        const displayButtons = filterDisplayButtons(res?.hideButtons);
        const disabledButtonMap = createDisabledButtonMap(res?.disableButtons);
        return { ...res, displayButtons, disabledButtonMap };
      },
      transformErrorResponse: (err, meta) => {
        const timedError = toTimeoutError(err, meta);
        const error = {
          ...timedError,
          message: `Error (${timedError?.status}): unable to access clock data`,
        };
        return error;
      },
      providesTags: () => {
        return [{ type: 'CLOCKCONNECT' }];
      },
      async onQueryStarted({ mobile, user }, { queryFulfilled }) {
        if (!mobile || !isNativeOfflineShell()) {
          return;
        }

        try {
          const { data } = await queryFulfilled;
          reportApiReachable();
          const userKey = getWebClockUserStorageKey(user);

          if (userKey) {
            await mergeOfflineClockConnectCache(userKey, data);
          }
        } catch (settled) {
          const queryError = settled?.error ?? settled;

          if (isTransportError(queryError)) {
            reportApiUnreachable();
          }
        }
      },
    }),
    finalizePunch: build.mutation({
      query: ({ body, mobile, punch, user }) => {
        const request = {
          url: `${config.clockServiceUrl}/${mobile ? 'MobilePunch' : 'Punch'}`,
          method: 'POST',
          headers: {
            jwt: user.token,
            punch,
          },
          body,
        };

        return mobile
          ? withRequestTimeout(request, PUNCH_SUBMIT_HARD_TIMEOUT_MS)
          : request;
      },
      transformResponse: async (res) => {
        return res;
      },
      transformErrorResponse: toTimeoutError,
      invalidatesTags: [{ type: 'CLOCKCONNECT' }],
    }),
    offlineMobilePunch: build.mutation({
      query: buildOfflineMobilePunchRequest,
      transformResponse: async (res) => res,
      transformErrorResponse: toTimeoutError,
    }),
    punchRequest: build.mutation({
      query: ({ mobile, overridePunch, punch, user }) => {
        const request = {
          url: `${config.clockServiceUrl}/PunchRequest`,
          method: 'POST',
          headers: {
            jwt: user.token,
            punch,
          },
          body: {
            overridePunch,
          },
        };

        return mobile
          ? withRequestTimeout(request, PUNCH_SUBMIT_HARD_TIMEOUT_MS)
          : request;
      },
      transformErrorResponse: toTimeoutError,
      transformResponse: async (res) => {
        const { RuleResult } = res;
        return RuleResult;
      },
      // invalidatesTags: [{ type: 'CLOCKCONNECT' }], // NOTE do not want to invalidate here
      // It gets invalidated in the finalizePunch mutation
    }),
    refetchClockConnect: build.mutation({
      queryFn: async () => ({ data: null }),
      invalidatesTags: [{ type: 'CLOCKCONNECT' }],
    }),
    postWhiteLabelBranding: build.mutation({
      query: ({ whiteLabelClient }) => {
        const url =
          `${config.clockServiceUrl}/SiteBranding?hostName=${whiteLabelClient}`.replace(
            /([^:]\/)\/+/g,
            '$1'
          );
        return {
          url,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {},
        };
      },
    }),
  }),
});

export const {
  useClockConnectQuery,
  useLazyClockConnectQuery,
  useFinalizePunchMutation,
  useOfflineMobilePunchMutation,
  usePunchRequestMutation,
  useRefetchClockConnectMutation,
  usePostWhiteLabelBrandingMutation,
} = webclockApi;

const filterDisplayButtons = (hideButtons) => {
  return buttonList.filter((button) => {
    if (button.value === 'ESS') {
      const enclosed = getUrlParameter('enclosed');
      const isEnclosedMode = hasValue(enclosed) ? JSON.parse(enclosed) : false;
      const show = getUrlParameter('showess');
      const showEss = hasValue(show) ? JSON.parse(show) : false;
      const essRedirectAllowed = !isEnclosedMode || showEss;
      return !hideButtons?.includes(button.value) && essRedirectAllowed;
    }
    return !hideButtons?.includes(button.value);
  });
};

const createDisabledButtonMap = (disabledButtons) => {
  return buttonList.reduce((acc, button) => {
    acc[button.value] = disabledButtons?.includes(button.value) || false;
    return acc;
  }, {});
};
